/**
 * Status → semantic tone maps for the Delivery Runs chips. Tone drives the
 * pill/dot colours (shared design tokens); every mapper is null-safe and falls
 * back to `muted` so an unknown wire value never throws or renders bare.
 */
import { type DeliveryPhase, IN_FLIGHT_RUN_STATUSES, type PipelineRunStatus } from './enums';

/** The five semantic tones a status chip can take. */
export type StatusTone = 'info' | 'success' | 'warning' | 'error' | 'muted';

/** Active in-flight phases — their chip dot pulses. */
const ACTIVE_PHASES = new Set<DeliveryPhase>([
  'VALIDATING',
  'PROCESSING',
  'DELIVERING',
  'RETRYING',
]);

/** UI lifecycle phase → tone (the value the run chip renders). */
export function phaseTone(phase: DeliveryPhase): StatusTone {
  switch (phase) {
    case 'COMPLETED':
      return 'success';
    case 'PARTIALLY_COMPLETED':
    case 'PAUSED':
    case 'RETRYING':
      return 'warning';
    case 'FAILED':
      return 'error';
    case 'VALIDATING':
    case 'PROCESSING':
    case 'DELIVERING':
      return 'info';
    default:
      return 'muted';
  }
}

/** `true` when a phase chip's dot should pulse (actively running). */
export function isPhasePulsing(phase: DeliveryPhase): boolean {
  return ACTIVE_PHASES.has(phase);
}

/** Run lifecycle status → tone. In-flight stages read as RUNNING (info). */
export function runStatusTone(status: PipelineRunStatus | null | undefined): StatusTone {
  if (status == null) return 'muted';
  if (status === 'RUNNING' || status === 'RETRYING' || IN_FLIGHT_RUN_STATUSES.has(status)) {
    return 'info';
  }
  switch (status) {
    case 'COMPLETED':
    case 'DELIVERED':
      return 'success';
    case 'PARTIAL_DELIVERY':
    case 'PAUSED':
      return 'warning';
    case 'FAILED':
    case 'DELIVERY_FAILED':
    case 'CANCELLED':
      return 'error';
    default:
      return 'muted';
  }
}

/** Per-product outcome → tone (accepts the raw wire string). */
export function productOutcomeTone(outcome: string): StatusTone {
  switch (outcome) {
    case 'DELIVERED':
    case 'INCLUDED':
      return 'success';
    case 'FAILED':
    case 'VALIDATION_FAILED':
      return 'error';
    case 'SKIPPED_UNCHANGED':
    case 'STALE':
    case 'EXCLUDED':
      return 'warning';
    case 'PENDING':
      return 'info';
    default:
      return 'muted';
  }
}

/** Destination sync-review verdict → tone. `PENDING` awaits a verdict (warn);
 *  `NOT_APPLICABLE`/`UNKNOWN` are neutral. */
export function syncStatusTone(status: string): StatusTone {
  const s = status.toUpperCase();
  if (s === 'APPROVED' || s === 'ACTIVE' || s === 'ELIGIBLE') return 'success';
  if (s === 'REJECTED' || s === 'DISAPPROVED') return 'error';
  if (s === 'PENDING' || s === 'PROCESSING' || s === 'UNDER_REVIEW') return 'warning';
  return 'muted';
}

/** Free-form stage/step status string → tone (timeline + work units). */
export function stageStatusTone(status: string): StatusTone {
  const s = status.toUpperCase();
  if (s.includes('FAIL') || s.includes('ERROR') || s === 'CANCELLED') return 'error';
  if (s.includes('COMPLETE') || s === 'DELIVERED' || s === 'SUCCEEDED' || s === 'DONE') {
    return 'success';
  }
  if (s.includes('SKIP') || s.includes('PAUSE')) return 'warning';
  if (s.includes('RUN') || s.includes('PROGRESS') || s.includes('PENDING')) return 'info';
  return 'muted';
}

/** Reconciliation verdict → tone. `SOURCE_MISMATCH` is amber (bookkeeping, not
 *  lost products); `SKIPPED` / `UNKNOWN` / anything unrecognised is neutral. */
export function reconciliationTone(status: string): StatusTone {
  switch (status) {
    case 'MATCH':
      return 'success';
    case 'MISMATCH':
      return 'error';
    case 'SOURCE_MISMATCH':
      return 'warning';
    default:
      return 'muted';
  }
}

/**
 * Meta per-product `ingestStatus` → tone. `UPLOADED` reads as info, not success:
 * Meta only queued the item, so the chip copy says "awaiting confirmation" and a
 * green pill would over-claim. `TRANSPORT_FAILED` is transient (retried
 * automatically) so it warns; `ROW_REJECTED` is permanent and errors.
 */
export function ingestStatusTone(status: string): StatusTone {
  switch (status) {
    case 'PERSISTED':
      return 'success';
    case 'UPLOADED':
    case 'CONFIRM_PENDING':
      return 'info';
    case 'WITHHELD':
    case 'AWAITING_OWNERSHIP':
    case 'TRANSPORT_FAILED':
      return 'warning';
    case 'ROW_REJECTED':
      return 'error';
    default:
      return 'muted';
  }
}

/** Pre-send validation verdict (`validationStatus`) → tone. */
export function validationStatusTone(status: string | null | undefined): StatusTone {
  switch (status) {
    case 'PASSED':
      return 'success';
    case 'FAILED':
      return 'error';
    case 'SKIPPED':
      return 'warning';
    default:
      return 'muted';
  }
}

/** Staged-feed upload status → tone (`AWAITING_APPROVAL` is the review gate). */
export function uploadStatusTone(status: string | null | undefined): StatusTone {
  const s = (status ?? '').toUpperCase();
  if (s === 'COMPLETED' || s === 'APPROVED' || s === 'DELIVERED') return 'success';
  if (s === 'FAILED' || s === 'CANCELLED' || s === 'REJECTED') return 'error';
  if (s === 'AWAITING_APPROVAL') return 'warning';
  if (s === 'PROCESSING' || s === 'DELIVERING' || s === 'INITIALIZED' || s === 'UPLOADING') {
    return 'info';
  }
  return 'muted';
}

/** Connection lifecycle status → tone (workspace header chip). */
export function connectionStatusTone(status: string | null | undefined): StatusTone {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'PAUSED':
      return 'warning';
    case 'INVALID':
    case 'ERROR':
    case 'REAUTH_REQUIRED':
      return 'error';
    case 'PENDING':
    case 'VALIDATING':
      return 'info';
    default:
      return 'muted';
  }
}
